import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Reveal } from "../lib/ui";

const int = (n) => Math.round(n || 0).toLocaleString();

// Brazil is the fair benchmark: a football nation built on small-sided volume, not on wealth.
export const BrazilBenchmark = () => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/brazil-benchmark").then((r) => setD(r.data)).catch(() => setErr("We could not load the Brazil benchmark. Refresh to try again."));
  }, []);

  if (err)
    return (
      <p className="mt-16 border border-maasai/40 bg-maasai/10 p-6 text-sm text-white" data-testid="brazil-error">
        {err}
      </p>
    );
  if (!d) return null;
  const per = d.per_million_after_programme;

  return (
    <section className="mt-20 border-t border-zinc-900 pt-14" data-testid="brazil-benchmark">
      <Reveal>
        <p className="eyebrow">Benchmarked against Brazil</p>
        <h2 className="display-2 mt-4 max-w-4xl">How much football Kenya is actually short of</h2>
        <p className="body-copy mt-6 max-w-3xl">
          Brazil built its technical base on small-sided volume rather than on wealth, which makes it the fair
          comparison. Its national register lists 789 football stadiums; its municipalities own 41,641 sports
          installations; commercial data verifies 1,911 futsal court businesses trading today. Set against
          population, that is the density this programme is measured against — and it is why the plan puts three
          courts, an upgraded 11-a-side ground, an academy and a stadium in every county rather than concentrating
          everything in Nairobi.
        </p>
      </Reveal>

      <div className="mt-8 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-3" data-testid="brazil-per-million">
        {[
          ["Small-sided courts per million", per.courts, "Brazil verifies 9.0 commercial futsal venues per million"],
          ["Upgraded 11-a-side grounds per million", per.pitch_upgrades, "One playable, floodlit ground per county"],
          ["Modular stadiums per million", per.stadiums, "Brazil registers 3.7 stadiums per million"],
        ].map(([label, value, note]) => (
          <div key={label} className="bg-[#0b0b0b] p-6">
            <p className="overline !text-zinc-500">{label}</p>
            <p className="mono mt-3 text-2xl font-bold text-ochre md:text-3xl">{value}</p>
            <p className="mt-2 text-[0.66rem] uppercase tracking-[0.14em] text-zinc-400">{note}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 overflow-x-auto border border-zinc-800" data-testid="brazil-table">
        <table className="w-full min-w-[900px] border-collapse text-left">
          <thead>
            <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
              {["Measure", "Brazil", "Per million", "Kenya would need", "Kenya today", "After this programme", "Share of Brazil", "Still short"].map((h) => (
                <th key={h} className="px-4 py-3 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-zinc-300">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r, i) => (
              <tr key={r.measure} className="border-b border-zinc-900" data-testid={`brazil-row-${i}`}>
                <td className="px-4 py-3 text-sm text-white">
                  {r.measure}
                  <a href={r.url} target="_blank" rel="noreferrer" className="mono mt-1 block text-[0.58rem] uppercase tracking-[0.14em] text-ochre underline underline-offset-4">
                    {r.source}
                  </a>
                </td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{int(r.brazil_count)}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{r.brazil_per_million}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{int(r.kenya_equivalent_needed)}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-500">{int(r.kenya_today)}</td>
                <td className="mono px-4 py-3 text-sm text-ochre">{int(r.kenya_after_programme)}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{r.share_of_brazil_pct}%</td>
                <td className="mono px-4 py-3 text-sm text-maasai">{int(r.gap_after_programme)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-4 max-w-4xl text-xs leading-relaxed text-zinc-400" data-testid="brazil-note">{d.note}</p>
    </section>
  );
};
